import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { T, useT } from "../i18n";
import { Kopf, KopfTrenn, Luft, Titel } from "../schale/Kopf";
import { FehlerBand, Laedt, Punkt } from "../bausteine";
import { useStart } from "../daten/haken";
import { useEinstellungen } from "../daten/haken";
import { useSitzung } from "../schale/Sitzung";
import { Fallspalte, spaltenAus } from "../bausteine/Fallspalten";
import { useFaelle } from "../daten/faelle";
import { useTelefon } from "../schale/geraet";
import { HeuteTelefon } from "./HeuteTelefon";
import { OhneRolle, PrueferStart } from "./Handy";
import { tagImBetrieb, wandImBetrieb, wochentagVon } from "../daten/betriebstag";
import { FristenKarte, HeuteRausKarte, UmsatzAusblick } from "./StartAusblick";

/* ══════════════════════════════════════════════════════════════════
   Die Startseite.

   Oben die Wege in den Tag — keine Knöpfe oben rechts, sondern Flächen,
   die man trifft, ohne zu suchen.

   **Zwei gleiche Kacheln, und die erste ist gefüllt.** Bis zum 17.09.2026
   war die erste auch die breiteste — die Direktvermietung ist der
   Regelfall (Regel 10), und Breite war dafür der Träger. Sie ist der
   falsche: Flächen, von denen eine anderthalbmal so groß ist, lesen sich
   als „eine und der Rest" und nicht als Wege nebeneinander. Der Regelfall
   trägt deshalb die Fläche in Hausgelb und dieselbe Größe wie sein
   Nachbar (28-designkonzept.md, „Aktion ist Fläche, Zustand ist Punkt").

   Dieser Satz stand hier seit dem 17.09. abends — `stile/start.css` trug
   bis zum 18.09.2026 trotzdem weiter `1.5fr 1fr`. Seitdem stimmen beide
   überein: **gleiche Fläche, ungleiches Gewicht.** Getragen wird das
   Gewicht allein von der Füllung, und zwar mit demselben Paar wie im
   Abschluss der Merkliste (`bausteine/MerklisteAbschluss.tsx`): „Jetzt
   vermieten" gefüllt, „Angebot" als Umriss daneben, Vermieten zuerst.

   **Hier beginnt ein Vorgang — beide Wege führen direkt in die
   Erfassung.** Kein Zwischenschirm, keine Frage vorweg (Regel 11): Der
   Zeitraum steht beim Öffnen schon da (`daten/vermietstand.ts`,
   `zeitVorgabe()` — ab morgen, fünf Miettage), die Bindefrist ebenso
   (`masken/vorgaenge/AngebotBauen.tsx`, aus den Vorgaben), und hat der
   gewählte Kunde genau einen Einsatzort, ist „Wohin" gesetzt und nicht
   gefragt. Der Unterschied zwischen den beiden Wegen ist allein, was am
   Ende entsteht: ein verbindlicher Auftrag oder ein Angebot mit Frist.

   **Keine Zahl auf den Kacheln.** Darunter tragen die Kennzahlen und die
   Karte „Zu prüfen" welche, und zwei davon zählen mit Absicht
   Verschiedenes. Eine dritte Zahl in derselben Form, nur weiter oben,
   läse sich als vierte Angabe derselben Reihe.

   **Beide Kacheln tun etwas.** Hier standen bis zum 17.09.2026 vier: zwei,
   die etwas tun (vermieten, anbieten), und zwei, die etwas beantworten
   (Preisauskunft, Verfügbarkeitscheck). Die beiden Auskünfte sind gefallen
   — der Grund steht unten an ihrer alten Stelle und in
   38-vermietmodul.md („Aus zwei Blättern wird eins"). Kurz: Eine Auskunft
   ist kein Ziel, das man ansteuert; sie kommt dorthin, wo gefragt wird
   (`masken/Nachschlagen.tsx`, aus jeder Maske über die Seitenleiste oder
   `Alt`+`N`).

   **„Interessent anlegen" ist weg.** Der Weg bleibt: In der Kundenliste
   öffnet „+ Kunde" dasselbe Blatt mit dem Umschalter Kunde · Interessent ·
   Vermieter (`bausteine/KundeAnlegen.tsx`), und `/kunden/neu?art=interessent`
   führt weiter direkt dorthin. Eine Kachel auf der Startseite braucht es
   dafür nicht: Wer einen Interessenten anlegt, tut das, weil einer
   angerufen hat — und dann steht er ohnehin schon bei den Kunden.

   Darunter vier Zahlen, die den Tag beschreiben. Alle vier kommen aus
   dem, was die App ohnehin führt — keine Zahl, die irgendwo hergeholt
   werden müsste. Der Umsatz steht als Strich, bis die Kalkulation aus
   04-preisfindung.md vorliegt.

   Dann vier gleichwertige Karten, **alle gleich hoch**. Eine Karte, die
   kürzer ist als ihre Nachbarin, sieht aus wie ein Fehler — auch wenn
   sie zu Recht leer ist. Darum hat jede einen eigenen Leersatz.
   ══════════════════════════════════════════════════════════════════ */

/* Das Wetter ist für einen Vermieter keine Spielerei: Regen heißt Ausfall,
   Frost heißt kein Erdbau, Sturm heißt kein Kran. Es ist derselbe Grund, der
   in der Rückfrage nach einer Verschiebung an erster Stelle steht. Der Ort
   steht in den Einstellungen — der Betriebshof, nicht der Sitz des Benutzers. */
const WETTER = [
  { tag: "heute", wort: "bewoelkt", art: "wolke", grad: "14°", wind: "12 km/h" },
  { tag: "morgen", wort: "regen", art: "regen", grad: "11°", wind: "24 km/h" },
  { tag: "uebermorgen", wort: "heiter", art: "sonne", grad: "18°", wind: "8 km/h" },
] as const;

/* Farbe trägt hier ausnahmsweise die Aussage mit: Eine gelbe Sonne und eine
   graue Wolke sind so gelernt, dass jede andere Zuordnung falsch wirkt.
   Die Form trägt trotzdem mit — Kreis, Wolke, Wolke mit Tropfen. */
function WetterZeichen({ art }: { art: "sonne" | "regen" | "wolke" }) {
  if (art === "sonne") {
    return (
      <svg viewBox="0 0 24 24" className="w-bild sonne" aria-hidden="true">
        <circle cx="12" cy="12" r="5" />
        <g className="strahlen">
          <path d="M12 1v3M12 20v3M1 12h3M20 12h3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M19.8 4.2l-2.1 2.1M6.3 17.7l-2.1 2.1" />
        </g>
      </svg>
    );
  }
  if (art === "regen") {
    return (
      <svg viewBox="0 0 24 24" className="w-bild regen" aria-hidden="true">
        <path className="wolkeform" d="M7 17h10a4 4 0 0 0 .4-8 6 6 0 0 0-11.5 1.6A3.5 3.5 0 0 0 7 17z" />
        <g className="tropfen"><path d="M8.5 19.5l-1 2.5M12 19.5l-1 2.5M15.5 19.5l-1 2.5" /></g>
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" className="w-bild wolke" aria-hidden="true">
      <path className="wolkeform" d="M7 18h10a4.5 4.5 0 0 0 .4-9 6.5 6.5 0 0 0-12.4 1.8A3.8 3.8 0 0 0 7 18z" />
    </svg>
  );
}

function Wetter({ ort }: { ort: string }) {
  const t = useT();
  const jetzt = WETTER[0];
  const wort = t(`wetter.${jetzt.wort}`);
  return (
    <span
      className="wetter"
      title={t("wetter.titel", { wort, grad: jetzt.grad, wind: jetzt.wind, ort })}
    >
      <WetterZeichen art={jetzt.art} />
      <span className="w-text">
        <b>{jetzt.grad}</b>
        <span>{wort} · {ort}</span>
      </span>
      <span className="w-morgen">
        {WETTER.slice(1).map((w) => (
          <span
            key={w.tag}
            className="w-tag"
            title={t("wetter.tagTitel", { tag: t(`wetter.${w.tag}`), wort: t(`wetter.${w.wort}`), grad: w.grad })}
          >
            <WetterZeichen art={w.art} />
            <i>{w.grad}</i>
          </span>
        ))}
      </span>
    </span>
  );
}

/** Die Uhr im Kopf. Sie läuft — eine stehende Uhr ist schlimmer als keine. */
function Jetzt() {
  const t = useT();
  const [jetzt, setzeJetzt] = useState(() => new Date());
  useEffect(() => {
    const uhr = window.setInterval(() => setzeJetzt(new Date()), 30_000);
    return () => window.clearInterval(uhr);
  }, []);

  const wochentage = t.liste("kalender.wochentageLang");
  const monate = t.liste("kalender.monate");
  // Die Uhr des Betriebs (N8-02) — unsere Liste beginnt am Montag.
  const wand = wandImBetrieb(jetzt);
  const tag = wochentage[wochentagVon(tagImBetrieb(jetzt))] ?? "";
  const zwei = (n: number) => String(n).padStart(2, "0");

  return (
    <span className="uhr">
      <b>{zwei(wand?.stunde ?? 0)}:{zwei(wand?.minute ?? 0)}</b>
      <span>
        {tag}<br />
        {wand?.tag}. {monate[(wand?.monat ?? 1) - 1]} {wand?.jahr}
      </span>
    </span>
  );
}

/**
 * Wie lange der Lexware-Abgleich her ist — in Worten, die jemand liest.
 *
 * Bis zum 17.09.2026 stand hier die rohe Zahl: *„Abgleich vor 98244 s"*.
 * Das ist richtig und sagt nichts — niemand rechnet 98.244 Sekunden im Kopf
 * in siebenundzwanzig Stunden um, und genau daran hängt die Frage, ob der
 * Abgleich noch läuft. Die Staffel ist grob, weil die Genauigkeit hier
 * nichts trägt: Wer wissen will, ob es heute Morgen war, braucht keine
 * Sekunden.
 */
function abgleichWort(sekunden: number): [string, Record<string, number>] {
  if (sekunden < 60) return ["start.abgleichEben", {}];
  if (sekunden < 3600) return ["start.abgleichMinuten", { zahl: Math.round(sekunden / 60) }];
  if (sekunden < 172800) return ["start.abgleichStunden", { zahl: Math.round(sekunden / 3600) }];
  return ["start.abgleichTagen", { zahl: Math.round(sekunden / 86400) }];
}

/**
 * **Eine Adresse, zwei Zeichnungen** (17.09.2026). Am Telefon steht an
 * derselben Stelle der Schirm der Rolle (`HeuteTelefon.tsx`), sonst diese
 * Startseite. Gefragt wird die Lage, nicht die Breite (`schale/geraet.ts`);
 * `/handy/start` leitet seitdem hierher.
 */
export function Heute() {
  return useTelefon() ? <HeuteTelefon /> : <HeuteTisch />;
}

/* ══════════════ Ein Modul, zwei Hälften — 21.09.2026 ══════════════

   Luca: *„Wie kann man heute und Start zusammenbringen? … Vor allem auf dem
   Desktop klingt das auch irgendwie dumm, weil man da dann irgendwie
   Tagesmappe und Start hat. Das ist ja irgendwie Quatsch."* Und: *„Manchmal
   wird ein Modul, und dann, je nach Rolle, verändert sich das Modul … Das
   ist ja auch: Wer hat welche Aufträge heute?"*

   Am Schreibtisch standen im Menü „Tagesmappe" und „Start" untereinander;
   die Mappe war dort eine 520-px-Spalte in einem 1440-px-Fenster, und die
   Startseite trug eine Karte „Heute" mit vier Zeilen aus derselben Liste.

   **Jetzt trägt dieser Schirm den Tag selbst**, sobald die Rolle ihn darf
   (`tagesmappe`): die Einsätze mit ihrem Stand, was frei ist — und, für die
   Disposition, was andere haben, mit Namen. Die alte Karte „Heute" bleibt
   für die Rollen ohne Tagesmappe (Außendienst, Buchhaltung): Sie zeigt
   Übergaben und Rücknahmen des Tages, und ohne sie verlören sie diese
   Auskunft ganz. */

/**
 * **Ohne Rolle derselbe Schirm wie am Telefon** (A-03, 03.10.2026). Am
 * Schreibtisch stand für ein Konto ohne jedes Recht die volle Startseite mit
 * Betriebszahlen und einer Kachel, die nichts tat. Die Weiche ist dieselbe wie
 * in `HeuteTelefon`: weder der Tag (`tagesmappe`) noch die Arbeit (`start`).
 */
function HeuteTisch() {
  const { darfRecht } = useSitzung();
  /* Der Prüfer hat weder Tag noch Arbeit — aber eine Rolle (P5-03). */
  if (!darfRecht("tagesmappe") && !darfRecht("start")) return darfRecht("pruefen") ? <PrueferStart /> : <OhneRolle />;
  return <HeuteTischInhalt />;
}

function HeuteTischInhalt() {
  const t = useT();
  const gehZu = useNavigate();
  const { data, isPending, isError, error, refetch } = useStart();
  const faelleAbfrage = useFaelle();
  const faelle = faelleAbfrage.data;
  /* **Ohne Antwort kein Leerzustand** (N10-03): „Alles verteilt" ist eine
     Aussage, und sie stand da, solange `GET /faelle` unterwegs war. */
  const faelleStand = {
    isPending: faelleAbfrage.isPending, isError: faelleAbfrage.isError, hatDaten: Boolean(faelle),
  };
  const { darf, darfAuf, darfRecht, ich } = useSitzung();
  const { data: einst } = useEinstellungen(darfRecht("verwalten"));
  /* Der Tag gehört dem Recht `tagesmappe` — dieselbe Grenze wie am Telefon
     (`HeuteTelefon.tsx`). Wer ihn nicht hat, sieht die Karte „Heute" weiter. */
  /* **Wer lenkt, verteilt** (24.09.2026): Disposition und Verwaltung sehen
     als zweite Spalte „Zu verteilen", alle anderen „Frei zum Übernehmen".
     Die Wiedervorlagen stehen seitdem als Fälle in „Meine Aufgaben" — am
     Tag, nicht vorher (Luca, 21.09.2026). */
  const lenkend = darf(["disposition", "verwaltung"]);

  /* „Umsatz Mai" stand als fester Text in de.json — ein Monat aus dem
     Prototyp. Gegen echte Daten ist das im September schlicht falsch.
     Welcher Monat gemeint ist, weiß die Uhr; der Name steht in de.json. */
  const monate = t.liste("kalender.monate");
  const jetzt = new Date();
  const dieserMonat = monate[jetzt.getMonth()] ?? "";
  const vormonat = monate[(jetzt.getMonth() + 11) % 12] ?? "";

  const kopf = (
    <Kopf>
      <Titel>{t("handy.titel")}</Titel>
      <KopfTrenn />
      <Jetzt />
      <Luft />
      <Wetter ort={einst?.wetter_ort ?? ich?.wetterOrt ?? ""} />
    </Kopf>
  );

  if (isPending) return <>{kopf}<Laedt zeilen={8} /></>;
  if (isError || !data) {
    return <>{kopf}<FehlerBand fehler={error} nochmal={() => void refetch()} /></>;
  }

  return (
    <>
      {kopf}
      <div className="start-raum">
        {/* Das Band „Neu zu prüfen" stand hier (24.09.2026, F-02). Seit
            demselben Tag trägt die Glocke oben rechts, was neu ist — über alle
            Geräte gelesen statt je Browser (Entscheidung K4, 51-meldungen.md). */}
        {/* Was man am Morgen anfangen will, steht vorn. */}
        <div className="anfangen">
          {/* **Was nicht geht, wird nicht angeboten** (28-designkonzept.md).
              `/vermieten` gehört dem Innendienst (`schale/rechte.ts`); der
              kaufmännische Außendienst darf anbieten, nicht vermieten. Am
              Telefon stand diese Prüfung seit dem 17.09.2026
              (`HeuteTelefon.tsx`), hier fehlte sie — und ein Fenster unter
              der Telefonbreite entscheidet nicht über die Rolle. Bleibt nur
              eine Kachel, nimmt sie die ganze Zeile (`stile/start.css`). */}
          {darfAuf("/vermieten") ? (
            <button className="losgehen haupt" type="button" onClick={() => void gehZu({ to: "/vermieten" })}>
              <span className="losgehen-was">{t("start.vermietenWas")}</span>
              <span className="losgehen-wie">{t("start.vermietenWie")}</span>
            </button>
          ) : null}
          {/* ── Zwei Kacheln, nicht vier (17.09.2026) ──────────────────
              Hier standen „Preisauskunft" und „Verfügbarkeitscheck".
              Luca, nachdem die Vermietfläche stand:

              > *„Ich verstehe jetzt mittlerweile nicht mehr, wozu ich dann
              > noch Preisauskunft und Verfügbarkeits-Check brauche. Warum
              > kann ich das nicht genau so gut dann auch in der
              > Direktvermietung machen und das jetzt merken?"*

              Von der Startseite aus ist der Weg auf die Fläche genauso
              kurz — und dort beantwortet eine Zeile beide Fragen und kann
              merken. Zwei Kacheln, die schlechter können, was die erste
              kann, sind zwei Kacheln zu viel.

              Das Blatt selbst gibt es weiter, für den einen Fall, für den
              es gebaut ist: den Blick aus einer **fremden** Maske heraus.
              Es hängt deshalb in der Schale und geht über die Seitenleiste
              oder `Alt`+`N` auf (`masken/Nachschlagen.tsx`). */}
          {/* Nur, wenn das Ziel erlaubt ist (A-03): eine Kachel, die nichts
              tut, sieht aus, als hinge die Anwendung. */}
          {darfAuf("/angebote/neu") ? (
            <button className="losgehen" type="button" onClick={() => void gehZu({ to: "/angebote/neu" })}>
              <span className="losgehen-was">{t("start.angebotWas")}</span>
              <span className="losgehen-wie">{t("start.angebotWie")}</span>
            </button>
          ) : null}
        </div>


        {/* Vier Zahlen, die den Tag beschreiben. Jede ist ein Knopf und führt
            dorthin, wo man etwas damit tun kann. */}
        <div className="kennzahlen">
          <button className="kz" type="button" onClick={() => void gehZu({ to: "/kalender" })}>
            <span className="kz-schild">{t("start.auslastung")}</span>
            <span className="kz-gross">{data.quote}<i>%</i></span>
            <span className="kz-balken"><i style={{ width: `${data.quote}%` }} /></span>
            <span className="kz-unten">
              <T k="start.auslastungUnten" werte={{
                belegt: data.belegt, alle: data.maschinen, frei: data.frei,
              }} />
            </span>
          </button>

          {/* **Mit dem Filter „Offen" in der Adresse** (22.09.2026): Die Kachel
              zählt, was läuft — die Liste öffnete aber in „Zu prüfen" und
              zeigte damit alles außer dem, worauf geklickt wurde. */}
          <button
            className="kz" type="button"
            onClick={() => void gehZu({ to: "/auftraege", search: { filter: "offen" } })}
          >
            <span className="kz-schild">{t("start.draussen")}</span>
            {/* **Die Zahl zählt Aufträge, nicht Geräte** (22.09.2026). Der
                Klick führt in die Auftragsliste, und dort ist ein Auftrag mit
                zwei Maschinen eine Zeile. Wie viele Geräte das sind, steht
                darunter — beides zugleich, keines statt des anderen. */}
            <span className="kz-gross">{data.auftraege_draussen}</span>
            <span className="kz-unten">
              <T k="start.draussenUnten" werte={{ geraete: data.draussen }} />
            </span>
            {data.zu_pruefen ? (
              <span className="kz-marke acht">
                <Punkt art="acht" />
                {t("start.draussenPruefen", { zahl: data.zu_pruefen })}
              </span>
            ) : null}
          </button>

          <button className="kz" type="button" onClick={() => void gehZu({ to: "/angebote" })}>
            <span className="kz-schild">{t("start.angeboteOffen")}</span>
            <span className="kz-gross">{data.angebote_offen}</span>
            {/* **Beim Kunden, und daneben die Entwürfe** (Prüfrunde Runde 10,
                N10-02). Die Kachel hieß „Angebote offen" und zeigte 0, der
                Reiter „Offen" der Liste zeigte 1 — dasselbe Wort, zwei
                Mengen. Die große Zahl bleibt, was das Handbuch verspricht:
                versendet, der Kunde ist am Zug. Die Entwürfe stehen darunter,
                und beide zusammen sind der Reiter. */}
            <span className="kz-unten">
              {data.angebote_entwurf
                ? <T k="start.angeboteUntenEntwurf" werte={{ zahl: data.angebote_entwurf }} />
                : t("start.angeboteUnten")}
            </span>
            {/* **Ring statt Rot** (24.09.2026, Prüfung F-02). Die ablaufende
                Bindefrist war die einzige rote Marke in den Kennzahlen — und
                damit das Lauteste auf der Seite, lauter als ein Protokoll, das
                auf die Disposition wartet. Rot heißt in Machine Office
                „blockiert"; ein Angebot, dessen Frist abläuft, blockiert
                nichts. Es ist ein Anruf, der fällig wird. */}
            {data.angebote_eilt ? (
              <span className="kz-marke acht">
                <Punkt art="acht" />
                {t("start.angeboteEilt", { zahl: data.angebote_eilt })}
              </span>
            ) : null}
          </button>

          {/* Der Umsatz des Monats stand hier als vierte Kachel. Seit dem
              08.10.2026 steht er als Balkendiagramm unten rechts in der
              zweiten Dreierreihe (`StartAusblick.tsx`) — Luca: *„Die
              Umsatz-Kachel als Balkendiagramm kommt unten rechts hin."* */}
        </div>

        {/* ══ Der Tag stand hier als breiter Block — und war falsch ══
            Luca, 21.09.2026 abends: *„Desktop ist jetzt ganz komisch
            integriert worden. Das geht gar nicht. Mache halt hier Spalten,
            was es zu tun gibt. Dann muss ich halt meine Aufgaben prüfen: Was
            hakt, was ist zu tun."*

            Die Tagesliste über die volle Breite war die Handyform in einem
            1200-px-Fenster: vier Abschnitte untereinander, jede Zeile ein
            Band. Am Schreibtisch arbeitet man in **Spalten** — was zu tun
            ist, nebeneinander, alles auf einen Blick. Der Tag selbst („läuft
            jetzt", „frei zum Übernehmen") gehört ans Telefon und aufs Brett;
            hier steht, was **mich** betrifft: meine Aufgaben, was hakt, was
            zu tun ist. */}

        {/* Vier gleichwertige Karten, alle gleich hoch. */}
        {/* ══ Drei Spalten statt vier Karten — 24.09.2026 (51-meldungen.md) ══
            Luca, 21.09.2026: *„Mache halt hier Spalten, was es zu tun gibt.
            Dann muss ich halt meine Aufgaben prüfen: Was hakt, was ist zu
            tun."* Und am 24.09.: *„Ist das nicht irgendwie alles ähnlich?"*
            Es war ähnlich — vier Karten, vier Merkmale, dieselbe Sache an bis
            zu fünf Stellen. Jetzt eine Liste aus dem Katalog der API und drei
            Spalten nach Rolle (`bausteine/Fallspalten.tsx`). „Was hakt" ist
            die rote Stufe in der passenden Spalte, die Fristen stehen unter
            „Demnächst". Der Zustand von Lexware Office steht als leiser
            Satz unter den Entscheidungen. */}
        {(() => {
          /* **Qualifikation und Prüftermin stehen unten** (Luca, 08.10.2026:
             *„Kranführerschein läuft ab … läuft unter ‚Meine Aufgaben' rein,
             und das macht ja keinen Sinn."*). Sie stehen in der zweiten Reihe
             (`StartAusblick.tsx`). Nur ein Gerät, das überfällig beim Kunden
             steht (`blockiert`), bleibt auch hier — das ist ein Handgriff. */
          const ohneAusblick = (faelle?.faelle ?? []).filter((f) =>
            f.art !== "qualifikation" && (f.art !== "prueftermin" || f.stufe === "blockiert"));
          const spalten = spaltenAus(ohneAusblick, faelle?.ich ?? "", faelle?.gruppen ?? [], lenkend);
          const lexStatus = (
            <>
              <Punkt art={data.lexware_wechsel ? "stopp"
                : data.lexware_zustand === "verbunden" ? "gut"
                : data.lexware_zustand === "abgelehnt" ? "stopp" : "acht"} />
              {" "}
              {t(data.lexware_wechsel ? "start.lexwareWechsel"
                : data.lexware_zustand === "verbunden" ? "start.lexwareVerbunden"
                : data.lexware_zustand === "abgelehnt" ? "start.lexwareAbgelehnt"
                  : data.lexware_zustand === "nicht_erreichbar" ? "start.lexwareStumm"
                    : "start.lexwareUngepflegt")}
              <span className="zeit">
                {data.lexware_abgleich_sekunden === null
                  ? t("start.lexwareOhneAbgleich")
                  : t(...abgleichWort(data.lexware_abgleich_sekunden))}
              </span>
            </>
          );
          return (
            <>
            <div className="start-spalten drei">
              <Fallspalte titel="fall.spalte.meine" faelle={spalten.meine} leer="fall.leer.meine" abfrage={faelleStand} />
              <Fallspalte
                titel={lenkend ? "fall.spalte.verteilen" : "fall.spalte.frei"}
                faelle={spalten.zweite}
                leer={lenkend ? "fall.leer.verteilen" : "fall.leer.frei"}
                abfrage={faelleStand}
                mitBesitzer={lenkend}
                weg={lenkend ? {
                  wort: "fall.zumBrett",
                  /* Eingeteilt wird seit dem 08.10.2026 auf eigener Seite (docs/65). */
                  beiKlick: () => void gehZu({ to: "/einteilen" }),
                } : undefined}
              />
              <HeuteRausKarte raus={data.ausblick.raus} />
            </div>
            {/* **Die zweite Reihe** (Luca, 08.10.2026): Zu entscheiden rückt
                nach unten links, daneben die Fristen und der Umsatz — gleich
                hoch wie die Reihe darüber (`stile/start.css`). */}
            <div className="start-spalten drei">
              <Fallspalte
                titel="fall.spalte.entscheiden"
                faelle={[...spalten.entscheiden, ...spalten.hinweise]}
                leer="fall.leer.entscheiden"
                abfrage={faelleStand}
                status={lexStatus}
              />
              <FristenKarte ausblick={data.ausblick} />
              <UmsatzAusblick
                ausblick={data.ausblick}
                monatJetzt={dieserMonat}
                vormonat={vormonat}
                umsatzBelege={data.umsatz_belege}
              />
            </div>
            </>
          );
        })()}
      </div>
    </>
  );
}
